import { useState, useCallback, useEffect } from 'react';
import { useApp, ROLES, ROLE_LABELS } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { StatusBadge } from '../../components/shared/StatusBadge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  IndianRupee,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  Calendar,
  AlertTriangle,
  FileText,
  Paperclip,
  Check,
  Upload,
  ArrowRight,
  Info,
  ChevronRight,
  User,
  Activity
} from 'lucide-react';
import { format } from 'date-fns';
import { getDownlineUserIds, mockRates } from '../../data/mockData';
import { fetchPayouts, fetchCases, fetchRates, submitPayoutRequest, updatePayoutStatus } from '../../api';

export default function PayoutRequests() {
  const {
    currentRole,
    currentUserId,
    penaltiesList,
    waiveOffRequestsList,
    submitWaiveOffRequest,
    updateWaiveOffRecommendation,
    finalizeWaiveOffDecision,
    addNotification,
    extraCostsList
  } = useApp();

  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [liveCases, setLiveCases] = useState([]);
  const [rates, setRates] = useState([]);
  const [requestAmount, setRequestAmount] = useState('');
  
  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [payRes, casesRes, ratesRes] = await Promise.all([
          fetchPayouts().catch(() => []),
          fetchCases().catch(() => []),
          fetchRates().catch(() => [])
        ]);
        if (isMounted) {
          if (Array.isArray(payRes)) setRequests(payRes);
          if (Array.isArray(casesRes)) setLiveCases(casesRes);
          if (Array.isArray(ratesRes)) setRates(ratesRes);
        }
      } catch (err) {
        console.error('Failed to load payout data:', err);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);

  // Waive-Off Modal and States
  const [isWaiveOffModalOpen, setIsWaiveOffModalOpen] = useState(false);
  const [selectedPenalties, setSelectedPenalties] = useState([]);
  const [waiveOffReason, setWaiveOffReason] = useState('');
  const [supportingComments, setSupportingComments] = useState('');
  const [mockAttachmentName, setMockAttachmentName] = useState('');

  // Manager/Admin decision states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionComment, setActionComment] = useState('');

  const activeUser = (() => {
    try {
      const stored = localStorage.getItem('crm_user');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return { id: currentUserId, name: 'User', role: currentRole };
  })();
  const isAdminView = [ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.MANAGER].includes(currentRole);

  // Group worked cases and dynamic month-wise payout calculations
  const downlineIds = getDownlineUserIds(currentUserId);
  const myWorkedCases = liveCases.filter(c => {
    const isAssigned = c.assignedTo?.id === currentUserId || c.assignedTo_id === currentUserId || downlineIds.includes(c.assignedTo?.id);
    const isCompleted = ['completed', 'report_sent', 'qc_approved'].includes(c.status);
    return isAssigned && isCompleted;
  });

  const getPayoutForCase = (c, role) => {
    const rateMatrix = rates.length > 0 ? rates : mockRates;
    const rateCard = rateMatrix.find(r => r.client?.id === c.client?.id && r.product?.id === c.product?.id);
    if (rateCard) {
      if (role === ROLES.FIELD_EXECUTIVE) return rateCard.fePayout || 150;
      if (role === ROLES.VENDOR) return rateCard.vendorPayout || 180;
      if (role === ROLES.BUSINESS_PARTNER) return rateCard.bpPayout || 140;
    }
    
    const basePrice = c.product?.basePrice || 250;
    if (role === ROLES.FIELD_EXECUTIVE) return Math.round(basePrice * 0.6);
    if (role === ROLES.VENDOR) return Math.round(basePrice * 0.72);
    if (role === ROLES.BUSINESS_PARTNER) return Math.round(basePrice * 0.5);
    return 150;
  };

  const getMonthYear = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMMM yyyy');
    } catch (e) {
      return 'May 2026';
    }
  };

  const caseMonths = Array.from(new Set(myWorkedCases.map(c => getMonthYear(c.createdAt || c.updatedAt))));
  if (caseMonths.length === 0) caseMonths.push('May 2026');

  const [selectedMonth, setSelectedMonth] = useState(caseMonths[0]);

  // Keep selectedMonth in sync with available months when changing roles/users
  useEffect(() => {
    if (!caseMonths.includes(selectedMonth)) {
      setSelectedMonth(caseMonths[0] || 'May 2026');
    }
  }, [currentUserId, currentRole, caseMonths, selectedMonth]);

  const monthCases = myWorkedCases.filter(c => getMonthYear(c.createdAt || c.updatedAt) === selectedMonth);
  const baseEarnings = monthCases.reduce((sum, c) => sum + getPayoutForCase(c, currentRole), 0);

  const getPenaltyMonthYear = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMMM yyyy');
    } catch (e) {
      return 'May 2026';
    }
  };

  const myPenalties = penaltiesList.filter(p => {
    const isForUser = p.userId === currentUserId || downlineIds.includes(p.userId);
    return isForUser;
  });

  const monthPenalties = myPenalties.filter(p => {
    const isDeducted = p.status === 'Deducted' || p.status === 'Waive Off Requested';
    const isThisMonth = getPenaltyMonthYear(p.date) === selectedMonth;
    return isDeducted && isThisMonth;
  });

  const getExtraCostMonthYear = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMMM yyyy');
    } catch (e) {
      return 'May 2026';
    }
  };

  const myExtraCosts = extraCostsList ? extraCostsList.filter(r => {
    const isForUser = r.requestedBy?.id === currentUserId || downlineIds.includes(r.requestedBy?.id);
    return isForUser;
  }) : [];

  const monthExtraCosts = myExtraCosts.filter(r => {
    const isThisMonth = getExtraCostMonthYear(r.date) === selectedMonth;
    return isThisMonth;
  });

  const pendingExtraCostsAmount = monthExtraCosts.filter(r => r.status === 'pending').reduce((sum, r) => sum + r.amount, 0);
  const approvedExtraCostsAmount = monthExtraCosts.filter(r => r.status === 'approved').reduce((sum, r) => sum + r.amount, 0);

  const totalPenaltiesAmount = monthPenalties.reduce((sum, p) => sum + p.amount, 0);
  const finalPayableAmount = Math.max(0, baseEarnings + approvedExtraCostsAmount - totalPenaltiesAmount);

  // Filter requests based on downline or role
  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => ['approved', 'rejected'].includes(r.status));

  // Downline filter for Waiver requests (Managers/SM can only see downline)
  const getVisibleWaiverRequests = useCallback(() => {
    if (currentRole === ROLES.ADMIN) return waiveOffRequestsList;
    if (currentRole === ROLES.SUPREME_MANAGER || currentRole === ROLES.MANAGER) {
      const downlineIds = getDownlineUserIds(currentUserId);
      return waiveOffRequestsList.filter(req => downlineIds.includes(req.userId));
    }
    return waiveOffRequestsList.filter(req => req.userId === currentUserId);
  }, [currentRole, currentUserId, waiveOffRequestsList]);

  const visibleWaiverRequests = getVisibleWaiverRequests();

  const handlePayoutAction = (id, newStatus) => {
    setRequests(prev => prev.map(req => req.id === id ? { ...req, status: newStatus } : req));
    addNotification({
      title: `Payout Request ${newStatus.toUpperCase()}`,
      message: `Payout request ${id} has been ${newStatus}.`,
      type: newStatus === 'approved' ? 'success' : 'danger',
      targetRole: 'field_executive'
    });
  };

  const submitPayoutRequest = () => {
    const amountToRequest = finalPayableAmount;
    if (!amountToRequest || amountToRequest <= 0) {
      alert('No payable amount available for this month.');
      return;
    }

    const alreadyExists = requests.some(r => r.requesterId === currentUserId && r.month === selectedMonth);
    if (alreadyExists) {
      alert(`A payout request for ${selectedMonth} has already been submitted.`);
      return;
    }

    const newReq = {
      id: `PR-${Date.now().toString().slice(-4)}`,
      requesterId: currentUserId,
      requesterName: activeUser.name,
      role: currentRole,
      amount: amountToRequest,
      month: selectedMonth,
      status: 'pending',
      date: format(new Date(), 'yyyy-MM-dd'),
    };

    setRequests([newReq, ...requests]);
    setRequestAmount('');
    addNotification({
      title: 'Payout Request Submitted',
      message: `${activeUser.name} submitted a ${currentRole.replace('_', ' ')} payout request for ${selectedMonth} of ₹${amountToRequest.toLocaleString()}`,
      type: 'info',
      targetRole: 'manager'
    });
    alert(`Payout request of ₹${amountToRequest.toLocaleString()} for ${selectedMonth} submitted successfully.`);
  };

  const handlePenaltySelection = (penaltyId) => {
    setSelectedPenalties(prev =>
      prev.includes(penaltyId)
        ? prev.filter(id => id !== penaltyId)
        : [...prev, penaltyId]
    );
  };

  const handleWaiveOffSubmit = () => {
    if (selectedPenalties.length === 0 || !waiveOffReason) {
      alert('Please select at least one penalty case and provide a reason.');
      return;
    }

    const selectedPenaltiesAmount = penaltiesList
      .filter(p => selectedPenalties.includes(p.id))
      .reduce((sum, p) => sum + p.amount, 0);

    const newRequest = {
      id: `WR-${Date.now().toString().slice(-4)}`,
      userId: currentUserId,
      requesterName: activeUser.name,
      penaltyIds: selectedPenalties,
      reason: waiveOffReason,
      comment: supportingComments,
      totalAmount: selectedPenaltiesAmount,
      status: 'Pending',
      date: format(new Date(), 'yyyy-MM-dd'),
      attachmentName: mockAttachmentName || null,
      timeline: [
        { role: 'Field Executive', action: 'Submitted', user: activeUser.name, date: format(new Date(), 'yyyy-MM-dd'), status: 'done', comment: supportingComments },
        { role: 'Manager', action: 'Pending Recommendation', user: '', date: '', status: 'pending' },
        { role: 'Supreme Manager', action: 'Pending Recommendation', user: '', date: '', status: 'upcoming' },
        { role: 'Admin', action: 'Pending Decision', user: '', date: '', status: 'upcoming' }
      ]
    };

    submitWaiveOffRequest(newRequest);
    setIsWaiveOffModalOpen(false);
    setSelectedPenalties([]);
    setWaiveOffReason('');
    setSupportingComments('');
    setMockAttachmentName('');
  };

  const handleRecommendation = (recommendation) => {
    if (!selectedRequest) return;
    const roleLabel = currentRole === ROLES.MANAGER ? 'Manager' : 'Supreme Manager';
    updateWaiveOffRecommendation(
      selectedRequest.id,
      roleLabel,
      recommendation,
      actionComment || 'Recommended successfully.',
      activeUser.name
    );
    setActionComment('');
    setSelectedRequest(null);
    alert(`Waiver recommendation submitted: ${recommendation}`);
  };

  const handleFinalDecision = (decision) => {
    if (!selectedRequest) return;
    finalizeWaiveOffDecision(
      selectedRequest.id,
      decision,
      actionComment || 'Final decision processed.',
      activeUser.name
    );
    setActionComment('');
    setSelectedRequest(null);
    alert(`Waiver request final decision: ${decision}`);
  };

  if (!isAdminView) {
    // FE / VENDOR / BUSINESS PARTNER (My Payouts View)
    return (
      <div className="space-y-6 animate-fade-in text-slate-800" data-testid="payout-fe-view">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">My Payouts & Penalties ({ROLE_LABELS[currentRole]})</h1>
            <p className="text-sm text-slate-500 mt-1">Submit payout requests and penalty waive-offs based on auto-fetched completed work</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-xs">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-semibold text-slate-600">Select Month:</span>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="rounded bg-slate-50 border border-slate-200 px-2 py-0.5 text-xs font-bold text-slate-800 outline-none cursor-pointer"
              >
                {caseMonths.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <Button
              onClick={() => setIsWaiveOffModalOpen(true)}
              disabled={myPenalties.filter(p => p.status === 'Deducted').length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold gap-2 shadow-lg shadow-amber-500/10 text-xs py-1.5 px-3 h-auto"
            >
              <AlertTriangle className="w-4 h-4" />
              Request Penalty Waive Off
            </Button>
          </div>
        </div>

        {/* Dynamic KPI Cards Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="crm-card p-5 bg-white border border-slate-200">
            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Completed Cases</p>
            <p className="text-2xl font-bold text-slate-900 mt-2">{monthCases.length}</p>
            <span className="text-[10px] text-emerald-600 mt-1 font-semibold flex items-center gap-1">
              <Check className="w-3 h-3"/> SLA Compliant in {selectedMonth}
            </span>
          </div>

          <div className="crm-card p-5 bg-white border border-slate-200">
            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Total Earnings</p>
            <p className="text-2xl font-bold text-slate-900 mt-2">₹{(baseEarnings + approvedExtraCostsAmount).toLocaleString()}</p>
            <span className="text-[10px] text-slate-400 mt-1">
              Base: ₹{baseEarnings.toLocaleString()} • Extra Cost: ₹{approvedExtraCostsAmount.toLocaleString()}
            </span>
          </div>

          <div className="crm-card p-5 bg-white border border-slate-200">
            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Penalty Deductions</p>
            <p className="text-2xl font-bold text-rose-600 mt-2">-₹{totalPenaltiesAmount.toLocaleString()}</p>
            <span className="text-[10px] text-rose-400 mt-1">
              {monthPenalties.length} active deducted penalties this month
            </span>
          </div>

          {currentRole !== ROLES.UNIVERSAL_QC && (
            <div className="crm-card p-5 bg-blue-50/50 border border-blue-200 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-blue-600/5 rounded-bl-full pointer-events-none"></div>
              <p className="text-xs text-blue-600 font-bold uppercase tracking-wider">Final Payable Amount</p>
              <p className="text-2xl font-extrabold text-blue-700 mt-2">₹{finalPayableAmount.toLocaleString()}</p>
              <button
                onClick={submitPayoutRequest}
                disabled={finalPayableAmount <= 0}
                className="mt-3 w-full py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-md shadow-blue-500/10 flex items-center justify-center gap-1 transition-colors"
              >
                <Send className="w-3 h-3" />
                Submit Payout Request ({selectedMonth})
              </button>
              <p className="text-[9px] text-slate-500 text-center mt-1.5 font-semibold">
                Breakdown: ₹{baseEarnings.toLocaleString()} (Base) + ₹{approvedExtraCostsAmount.toLocaleString()} (Extra Cost) - ₹{totalPenaltiesAmount.toLocaleString()} (Penalty)
              </p>
            </div>
          )}
        </div>

        {/* Worked Cases & Penalty Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="worked" className="w-full bg-white border border-slate-200 rounded-xl p-5 crm-card">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                <TabsList className="bg-slate-50 p-1 border border-slate-200">
                  <TabsTrigger value="worked" className="text-xs font-bold px-3 py-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                    Worked Cases ({monthCases.length})
                  </TabsTrigger>
                  <TabsTrigger value="extra-costs" className="text-xs font-bold px-3 py-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                    Extra Costs ({monthExtraCosts.length})
                  </TabsTrigger>
                  <TabsTrigger value="penalties" className="text-xs font-bold px-3 py-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                    Deducted Penalties ({monthPenalties.length})
                  </TabsTrigger>
                </TabsList>
                <span className="text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-full">
                  Billing Period: {selectedMonth}
                </span>
              </div>

              {/* Tab Content: Worked Cases */}
              <TabsContent value="worked" className="mt-0 outline-none">
                {monthCases.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">
                    No completed cases found for {selectedMonth}.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                        <tr>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Case ID</th>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Client</th>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Verification Type</th>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Calculated Payout</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {monthCases.map((c) => (
                          <tr key={c.id} className="hover:bg-slate-50/30 bg-white">
                            <td className="px-4 py-3 font-semibold text-blue-600">{c.id}</td>
                            <td className="px-4 py-3 font-semibold text-slate-700">{c.client?.name || 'HDFC Bank'}</td>
                            <td className="px-4 py-3 text-slate-500 font-medium">{c.verificationType || 'Residence Verification'}</td>
                            <td className="px-4 py-3 font-bold text-emerald-600">₹{getPayoutForCase(c, currentRole).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              {/* Tab Content: Extra Costs */}
              <TabsContent value="extra-costs" className="mt-0 outline-none">
                {monthExtraCosts.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">
                    No extra cost requests found for {selectedMonth}.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                        <tr>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Case Ref</th>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Description</th>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Amount</th>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {monthExtraCosts.map((ec) => (
                          <tr key={ec.id} className="hover:bg-slate-50/30 bg-white">
                            <td className="px-4 py-3 font-semibold text-blue-600">{ec.caseRef}</td>
                            <td className="px-4 py-3 text-slate-700 font-medium">{ec.reason}</td>
                            <td className="px-4 py-3 font-bold text-emerald-600">₹{ec.amount.toLocaleString()}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
                                ec.status === 'approved' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                                ec.status === 'pending' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-red-50 text-rose-600 border-red-100'
                              }`}>
                                {ec.status.charAt(0).toUpperCase() + ec.status.slice(1)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              {/* Tab Content: Penalties */}
              <TabsContent value="penalties" className="mt-0 outline-none">
                {monthPenalties.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">
                    No active penalties deducted for {selectedMonth}.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                        <tr>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Case ID</th>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Reason</th>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Amount</th>
                          <th className="px-4 py-2.5 font-semibold text-xs uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {monthPenalties.map((penalty) => (
                          <tr key={penalty.id} className="hover:bg-slate-50/30 bg-white">
                            <td className="px-4 py-3 font-semibold text-blue-600">{penalty.caseId}</td>
                            <td className="px-4 py-3 text-slate-700 font-medium">{penalty.reason}</td>
                            <td className="px-4 py-3 font-bold text-rose-600">₹{penalty.amount.toLocaleString()}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-600 border border-rose-100">
                                {penalty.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Request Payout / Waiver History & Timeline */}
          <div className="crm-card p-6 bg-white border border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              Waive-Off Request Logs
            </h3>

            {visibleWaiverRequests.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">
                No waive-off requests submitted yet.
              </div>
            ) : (
              <div className="space-y-4 max-h-[450px] overflow-y-auto pr-1">
                {visibleWaiverRequests.map((req) => (
                  <div key={req.id} className="p-4 rounded-xl border border-slate-100 bg-slate-50 space-y-3 shadow-sm hover:border-slate-200 transition-all">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">ID: {req.id}</span>
                        <p className="text-sm font-bold text-slate-800 mt-0.5">Waiver for ₹{req.totalAmount.toLocaleString()}</p>
                      </div>
                      <Badge className={`${
                        req.status === 'Approved'
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-50'
                          : req.status === 'Rejected'
                          ? 'bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-50'
                          : 'bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-50'
                      }`} variant="outline">
                        {req.status}
                      </Badge>
                    </div>

                    <div className="text-xs text-slate-500 border-b border-slate-100 pb-2">
                      <p><strong className="font-semibold text-slate-600">Reason:</strong> {req.reason}</p>
                      {req.comment && <p className="mt-1 italic">"{req.comment}"</p>}
                    </div>

                    {/* Timeline */}
                    <div className="space-y-3 pt-1">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Approval Timeline</p>
                      <div className="relative border-l-2 border-slate-200 ml-2.5 pl-4 space-y-3">
                        {req.timeline.map((step, idx) => {
                          const isDone = step.status === 'done';
                          const isPending = step.status === 'pending';
                          
                          let dotColor = 'bg-slate-200 border-slate-300';
                          if (isDone) dotColor = step.recommendation === 'Rejected' || step.action.includes('Rejected') ? 'bg-rose-500 border-rose-200' : 'bg-emerald-500 border-emerald-200';
                          if (isPending) dotColor = 'bg-amber-400 border-amber-100 animate-pulse';

                          return (
                            <div key={idx} className="relative text-xs">
                              {/* Dot */}
                              <span className={`absolute -left-[23px] top-0.5 w-3.5 h-3.5 rounded-full border-2 ${dotColor} z-10 flex items-center justify-center`}>
                                {isDone && <Check className="w-2 h-2 text-white" />}
                              </span>
                              
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className={`font-semibold ${isDone ? 'text-slate-800' : isPending ? 'text-amber-600' : 'text-slate-400'}`}>
                                    {step.role}
                                  </p>
                                  <p className="text-[10px] text-slate-500">{step.action}</p>
                                </div>
                                {isDone && <span className="text-[9px] text-slate-400 font-semibold">{step.date}</span>}
                              </div>
                              {isDone && step.comment && (
                                <p className="mt-0.5 text-[10px] text-slate-500 bg-white border border-slate-100 rounded p-1 italic">
                                  {step.user}: "{step.comment}"
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* WAIVE OFF REQUEST MODAL */}
        <Dialog open={isWaiveOffModalOpen} onOpenChange={setIsWaiveOffModalOpen}>
          <DialogContent className="bg-white border-slate-200 max-w-md text-slate-800">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Request Penalty Waive Off
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-3">
              {/* Select Penalty Cases */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Select Penalty Cases to Waive Off</Label>
                <div className="max-h-[150px] overflow-y-auto border border-slate-200 rounded-lg p-2.5 space-y-2 divide-y divide-slate-100 bg-slate-50">
                  {myPenalties.filter(p => p.status === 'Deducted').map(penalty => (
                    <div key={penalty.id} className="flex items-center gap-3 pt-2 first:pt-0">
                      <input
                        type="checkbox"
                        id={penalty.id}
                        checked={selectedPenalties.includes(penalty.id)}
                        onChange={() => handlePenaltySelection(penalty.id)}
                        className="rounded border-slate-300 w-4 h-4 accent-blue-600 cursor-pointer"
                      />
                      <label htmlFor={penalty.id} className="flex-1 text-xs font-medium cursor-pointer text-slate-700 flex justify-between">
                        <div>
                          <span className="font-bold text-blue-600 block">{penalty.caseId}</span>
                          <span className="text-slate-500">{penalty.reason}</span>
                        </div>
                        <span className="font-bold text-rose-600 ml-2">₹{penalty.amount}</span>
                      </label>
                    </div>
                  ))}
                  {myPenalties.filter(p => p.status === 'Deducted').length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-4">No active deducted penalties available.</p>
                  )}
                </div>
              </div>

              {/* Reason for Waive Off */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-slate-700">Reason for Waive Off</Label>
                <select
                  value={waiveOffReason}
                  onChange={(e) => setWaiveOffReason(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50/50 p-2 text-xs font-medium focus:ring-2 focus:ring-blue-500 text-slate-700 outline-none"
                >
                  <option value="">-- Choose Reason --</option>
                  <option value="Client Delay / Rescheduled Visit">Client Delay / Rescheduled Visit</option>
                  <option value="SLA Mismatch in System">SLA Mismatch in System</option>
                  <option value="Technical GPS / Network Issue">Technical GPS / Network Issue</option>
                  <option value="Medical / Personal Emergency">Medical / Personal Emergency</option>
                  <option value="Other (Specify in comments)">Other (Specify in comments)</option>
                </select>
              </div>

              {/* Supporting Comments */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-slate-700">Supporting Comments</Label>
                <Textarea
                  placeholder="Explain why this penalty should be waived off..."
                  value={supportingComments}
                  onChange={(e) => setSupportingComments(e.target.value)}
                  className="bg-slate-50/50 border-slate-200 text-xs text-slate-800"
                  rows={3}
                />
              </div>

              {/* Mock File Upload */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-slate-700">Attachment (Optional)</Label>
                <div className="border border-dashed border-slate-200 rounded-lg p-4 text-center bg-slate-50 hover:bg-slate-100/50 transition-colors relative cursor-pointer">
                  <input
                    type="file"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setMockAttachmentName(e.target.files[0].name);
                      }
                    }}
                  />
                  <Upload className="w-6 h-6 mx-auto text-slate-400 mb-1.5" />
                  <p className="text-xs text-slate-600 font-semibold">
                    {mockAttachmentName ? `Attached: ${mockAttachmentName}` : 'Upload supporting documents (PDF, JPG)'}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Drag and drop or click to upload</p>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleWaiveOffSubmit}
                disabled={selectedPenalties.length === 0 || !waiveOffReason}
                className="w-full bg-blue-600 hover:bg-blue-700 font-semibold gap-2 mt-2"
              >
                <Send className="w-4 h-4" />
                Submit Waiver Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ADMIN / MANAGER / SUPREME MANAGER VIEW
  return (
    <div className="space-y-6 animate-fade-in text-slate-800" data-testid="payout-admin-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Payout & Penalty Administration</h1>
          <p className="text-sm text-slate-500 mt-1">Manage monthly payout requests and penalty waive-off submissions across your hierarchy</p>
        </div>
      </div>

      {/* Admin stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="crm-card p-4 flex items-center gap-4 bg-white border border-slate-200">
          <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{pendingRequests.length}</p>
            <p className="text-xs text-slate-500 font-semibold">Pending Payout Requests</p>
          </div>
        </div>

        <div className="crm-card p-4 flex items-center gap-4 bg-white border border-slate-200">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">
              {visibleWaiverRequests.filter(req => req.status === 'Pending').length}
            </p>
            <p className="text-xs text-slate-500 font-semibold">Pending Waive-Off Requests</p>
          </div>
        </div>

        <div className="crm-card p-4 flex items-center gap-4 bg-white border border-slate-200">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{processedRequests.length}</p>
            <p className="text-xs text-slate-500 font-semibold">Processed History</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 w-full md:w-auto">
          <TabsTrigger value="pending" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
            Payout Requests ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="waivers" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
            Penalty Waive-Offs ({visibleWaiverRequests.filter(req => req.status === 'Pending').length})
          </TabsTrigger>
          <TabsTrigger value="processed" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
            Processed Payouts
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Payout Requests */}
        <TabsContent value="pending" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingRequests.map(req => (
              <div key={req.id} className="crm-card p-5 bg-white border border-slate-200 flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">{req.requesterName}</h3>
                      <Badge variant="outline" className="mt-1 bg-blue-50 text-blue-600 border-none text-[10px] uppercase font-semibold">
                        {req.role.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="font-extrabold text-lg text-blue-600">₹{req.amount.toLocaleString()}</p>
                  </div>
                  <div className="space-y-1.5 my-4 text-xs border-y border-slate-100 py-3">
                    <div className="flex justify-between text-slate-500">
                      <span>Month:</span><span className="text-slate-800 font-semibold">{req.month}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>Date:</span><span className="text-slate-800 font-semibold">{req.date}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>Req ID:</span><span className="text-slate-800 font-semibold">{req.id}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={() => handlePayoutAction(req.id, 'approved')} className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-1.5 text-white font-semibold text-xs py-2">
                    <CheckCircle2 className="w-3.5 h-3.5"/> Approve
                  </Button>
                  <Button onClick={() => handlePayoutAction(req.id, 'rejected')} variant="outline" className="flex-1 border-red-200 text-rose-600 hover:bg-red-50 hover:text-red-700 gap-1.5 font-semibold text-xs py-2">
                    <XCircle className="w-3.5 h-3.5"/> Reject
                  </Button>
                </div>
              </div>
            ))}
            {pendingRequests.length === 0 && (
              <div className="col-span-full crm-card p-12 text-center text-slate-400 border-dashed bg-slate-50">
                No pending payout requests found.
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab 2: Penalty Waive-Off Requests Console */}
        <TabsContent value="waivers" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleWaiverRequests.map(req => {
              // Determine active step role
              const activeStep = req.timeline.find(s => s.status === 'pending');
              const isDecisionActive = activeStep && activeStep.role === (
                currentRole === ROLES.ADMIN ? 'Admin' :
                currentRole === ROLES.SUPREME_MANAGER ? 'Supreme Manager' : 'Manager'
              );

              return (
                <div key={req.id} className="crm-card p-5 bg-white border border-slate-200 flex flex-col justify-between space-y-4">
                  <div>
                    <div className="flex items-start justify-between border-b border-slate-100 pb-2">
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                          <User className="w-4 h-4 text-blue-500" />
                          {req.requesterName}
                        </h4>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">ID: {req.id}</span>
                      </div>
                      <Badge className="bg-amber-50 text-amber-600 border border-amber-200" variant="outline">
                        ₹{req.totalAmount.toLocaleString()} Waiver
                      </Badge>
                    </div>

                    <div className="space-y-2 mt-3 text-xs">
                      <p><strong className="font-semibold text-slate-700">Reason:</strong> {req.reason}</p>
                      {req.comment && <p className="bg-slate-50 p-2 border border-slate-100 rounded text-slate-500 italic mt-1">"{req.comment}"</p>}
                      {req.attachmentName && (
                        <p className="flex items-center gap-1 text-[10px] text-blue-600 font-bold bg-blue-50 w-fit p-1 rounded mt-2">
                          <Paperclip className="w-3 h-3" />
                          {req.attachmentName}
                        </p>
                      )}
                    </div>

                    {/* Timeline logs */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Hierarchy Timeline</p>
                      <div className="relative border-l-2 border-slate-200 ml-2 pl-3 space-y-2 text-[11px]">
                        {req.timeline.map((step, sIdx) => (
                          <div key={sIdx} className="relative">
                            <span className={`absolute -left-[18px] top-0.5 w-2.5 h-2.5 rounded-full border ${
                              step.status === 'done'
                                ? step.recommendation === 'Approved' ? 'bg-emerald-500 border-emerald-200' : 'bg-rose-500 border-rose-200'
                                : step.status === 'pending'
                                ? 'bg-amber-400 border-amber-200 animate-pulse'
                                : 'bg-slate-200 border-slate-300'
                            }`} />
                            <div className="flex justify-between">
                              <span className="font-semibold text-slate-700">{step.role}</span>
                              <span className="text-[9px] text-slate-400">{step.date}</span>
                            </div>
                            <p className="text-[9px] text-slate-500">{step.action}</p>
                            {step.status === 'done' && step.comment && (
                              <p className="text-[9px] text-slate-400 italic mt-0.5">"{step.user}: {step.comment}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-100">
                    {isDecisionActive ? (
                      <Button
                        onClick={() => {
                          setSelectedRequest(req);
                          setActionComment('');
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs py-2 shadow-sm flex items-center justify-center gap-1"
                      >
                        <Activity className="w-3.5 h-3.5" />
                        Action Decision Console
                      </Button>
                    ) : (
                      <div className="text-center text-[10px] text-slate-400 bg-slate-50 py-2 border border-slate-100 rounded-lg">
                        {req.status === 'Pending' ? 'Waiting for previous role recommendations' : `Final Status: ${req.status}`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {visibleWaiverRequests.length === 0 && (
              <div className="col-span-full crm-card p-12 text-center text-slate-400 border-dashed bg-slate-50">
                No waive-off requests listed in your downline range.
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab 3: Processed Payouts History */}
        <TabsContent value="processed" className="mt-6">
          <div className="crm-card p-0 overflow-hidden bg-white border border-slate-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                  <tr>
                    <th className="px-6 py-4 font-semibold text-xs uppercase">Request ID</th>
                    <th className="px-6 py-4 font-semibold text-xs uppercase">Requester</th>
                    <th className="px-6 py-4 font-semibold text-xs uppercase">Amount</th>
                    <th className="px-6 py-4 font-semibold text-xs uppercase">Month</th>
                    <th className="px-6 py-4 font-semibold text-xs uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {processedRequests.map(req => (
                    <tr key={req.id} className="hover:bg-slate-50/50 bg-white">
                      <td className="px-6 py-4 font-bold text-slate-900">{req.id}</td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-slate-900">{req.requesterName}</div>
                        <div className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">{req.role.replace('_', ' ')}</div>
                      </td>
                      <td className="px-6 py-4 font-extrabold text-slate-900">₹{req.amount.toLocaleString()}</td>
                      <td className="px-6 py-4 text-slate-500 font-medium">{req.month}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          req.status === 'approved'
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-150'
                            : 'bg-rose-50 text-rose-600 border border-rose-150'
                        }`}>
                          {req.status === 'approved' ? 'Approved' : 'Rejected'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {processedRequests.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-slate-400 bg-white">No processed requests.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ACTION REVIEW CONSOLE DIALOG */}
      <Dialog open={!!selectedRequest} onOpenChange={(open) => { if (!open) setSelectedRequest(null); }}>
        <DialogContent className="bg-white border-slate-200 max-w-md text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-500" />
              Action Decision Console
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500 font-medium">FE Requester:</span>
                  <span className="font-bold text-slate-800">{selectedRequest.requesterName}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500 font-medium">Request Date:</span>
                  <span className="font-semibold text-slate-700">{selectedRequest.date}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500 font-medium">Requested Waiver:</span>
                  <span className="font-extrabold text-blue-600">₹{selectedRequest.totalAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500 font-medium">Reason Statement:</span>
                  <span className="font-semibold text-slate-700">{selectedRequest.reason}</span>
                </div>
                {selectedRequest.comment && (
                  <div className="text-[11px] text-slate-500 border-t border-slate-200/60 pt-2 italic">
                    Comments: "{selectedRequest.comment}"
                  </div>
                )}
              </div>

              {/* Select Case Penalty details list */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Associated Penalty Cases</Label>
                <div className="space-y-1.5 max-h-[100px] overflow-y-auto pr-1">
                  {penaltiesList.filter(p => selectedRequest.penaltyIds.includes(p.id)).map(penalty => (
                    <div key={penalty.id} className="p-2 border border-slate-150 rounded bg-white text-xs flex justify-between items-center shadow-xs">
                      <div>
                        <span className="font-bold text-blue-600 block">{penalty.caseId}</span>
                        <span className="text-slate-500 text-[10px]">{penalty.reason}</span>
                      </div>
                      <span className="font-bold text-rose-600">₹{penalty.amount}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Feedback comment input */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-slate-700">Comments / Justification</Label>
                <Textarea
                  placeholder="Enter comments for this recommendation/decision..."
                  value={actionComment}
                  onChange={(e) => setActionComment(e.target.value)}
                  className="bg-slate-50/50 border-slate-200 text-xs"
                  rows={3}
                />
              </div>

              {/* Decision actions */}
              <div className="flex gap-3 pt-2">
                {currentRole === ROLES.ADMIN ? (
                  <>
                    <Button
                      onClick={() => handleFinalDecision('Approved')}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-semibold gap-1 text-white text-xs py-2 shadow-sm shadow-emerald-500/10"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Final Approve
                    </Button>
                    <Button
                      onClick={() => handleFinalDecision('Rejected')}
                      className="flex-1 bg-rose-600 hover:bg-rose-700 font-semibold gap-1 text-white text-xs py-2 shadow-sm shadow-rose-500/10"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Final Reject
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => handleRecommendation('Approved')}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-semibold gap-1 text-white text-xs py-2 shadow-sm"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Recommend Approve
                    </Button>
                    <Button
                      onClick={() => handleRecommendation('Rejected')}
                      className="flex-1 bg-rose-600 hover:bg-rose-700 font-semibold gap-1 text-white text-xs py-2 shadow-sm"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Recommend Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
